import { Component } from '@theme/component';

/**
 * @typedef {Object} CustomCollectionSliderRefs
 * @property {HTMLElement} track
 * @property {HTMLElement[]} cards
 * @property {HTMLButtonElement} [previousButton]
 * @property {HTMLButtonElement} [nextButton]
 */

/** @extends {Component<CustomCollectionSliderRefs>} */
class CustomCollectionSlider extends Component {
  #cardStep = 335; /* card width 315 + gap 20 */
  #scrollListener = null;
  #resizeListener = null;
  #intersectionObserver = null;

  connectedCallback() {
    super.connectedCallback();

    // Wait for layout before measuring card width and updating button visual state
    requestAnimationFrame(() => {
      this.#cardStep = this.#computeStep();
      this.#updateButtons();
      this.#observeCardVisibility();
    });

    this.#scrollListener = () => this.#updateButtons();
    this.refs.track?.addEventListener('scroll', this.#scrollListener, { passive: true });

    this.#resizeListener = () => {
      this.#cardStep = this.#computeStep();
      this.#updateButtons();
    };
    window.addEventListener('resize', this.#resizeListener, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#scrollListener) this.refs.track?.removeEventListener('scroll', this.#scrollListener);
    if (this.#resizeListener) window.removeEventListener('resize', this.#resizeListener);
    if (this.#intersectionObserver) this.#intersectionObserver.disconnect();
  }

  #observeCardVisibility() {
    const track = this.refs.track;
    const cards = this.refs.cards;
    if (!track || !cards || cards.length === 0) return;

    if (this.#intersectionObserver) this.#intersectionObserver.disconnect();

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const visibility = entry.intersectionRatio >= 0.98 ? 'full' : 'partial';
          entry.target.setAttribute('data-visibility', visibility);
        }
      },
      {
        root: track,
        threshold: [0, 0.5, 0.98, 1],
      }
    );

    for (const card of cards) {
      this.#intersectionObserver.observe(card);
    }
  }

  previous() {
    const track = this.refs.track;
    if (!track) return;
    const step = this.#computeStep();
    track.scrollBy({ left: -step, behavior: 'smooth' });
  }

  next() {
    const track = this.refs.track;
    if (!track) return;
    const step = this.#computeStep();
    track.scrollBy({ left: step, behavior: 'smooth' });
  }

  #computeStep() {
    const cards = this.refs.cards;
    if (!cards || cards.length < 1) return this.#cardStep;
    const first = cards[0];
    const second = cards[1];
    if (second) return second.offsetLeft - first.offsetLeft;
    return first.offsetWidth + 20;
  }

  #updateButtons() {
    const track = this.refs.track;
    if (!track) return;
    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
    // Use aria-disabled (visual only) so clicks still fire and the user can always trigger scroll
    if (this.refs.previousButton) {
      this.refs.previousButton.setAttribute('aria-disabled', atStart ? 'true' : 'false');
    }
    if (this.refs.nextButton) {
      this.refs.nextButton.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    }
  }
}

if (!customElements.get('custom-collection-slider')) {
  customElements.define('custom-collection-slider', CustomCollectionSlider);
}
